import { collection, addDoc, getDocs, doc, getDoc, updateDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Session, UserJourney, ImageData, Conversation } from '../types';
import { getBoards, createBoard, createTask } from './projectService';

// API base URL - Change this when deploying to production
const API_BASE_URL = 'https://deceptive-design-server-d4v-client-413055756357.europe-west1.run.app';
// For local development, use:
// const API_BASE_URL = 'http://localhost:8080';

// Database collection name
const SESSIONS_COLLECTION = 'sessions-d4v-dark-pattern';
const USERS_COLLECTION = 'report-user-ids';

// Sessions
export const createSession = async (
  sessionName: string,
  createdBy: string,
  domainName: string,
  adminId: string,
  userRole: string,
  type: string = 'dark_pattern'
): Promise<string> => {
  try {
    const sessionData = {
      sessionName,
      createdBy,
      createdAt: serverTimestamp(),
      domainName,
      demo: false,
      type,
      adminId, // Organization ID for multi-tenant access control
      userRole, // User role for additional context
      createdByRole: userRole // Store the role at creation time
    };

    const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), sessionData);
    const sessionId = docRef.id;

    // Assign session to user with proper organization mapping
    await assignSessionToUser(createdBy, sessionId, domainName, adminId);

    return sessionId;
  } catch (error) {
    console.error('Error creating session:', error);
    throw new Error('Failed to create session');
  }
};

// Assign session to user in report-user-ids collection
export const assignSessionToUser = async (
  userEmail: string,
  sessionId: string,
  domainName: string,
  adminId: string
): Promise<void> => {
  try {
    // First check if it's an admin user
    const adminRef = doc(db, USERS_COLLECTION, adminId);
    const adminDoc = await getDoc(adminRef);

    if (adminDoc.exists() && adminDoc.data().email === userEmail) {
      // User is an admin - assign to admin document
      const userData = adminDoc.data();
      const currentSessions = userData.sessions || [];

      if (!currentSessions.includes(sessionId)) {
        const updatedSessions = [...currentSessions, sessionId];
        await updateDoc(adminRef, {
          sessions: updatedSessions
        });
      }
    } else {
      // Check if it's a team member under this admin
      const usersRef = collection(db, USERS_COLLECTION, adminId, 'users');
      const q = query(usersRef, where('email', '==', userEmail));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        querySnapshot.docs.forEach(async (userDoc) => {
          const userData = userDoc.data();
          const currentSessions = userData.sessions || [];

          if (!currentSessions.includes(sessionId)) {
            const updatedSessions = [...currentSessions, sessionId];
            await updateDoc(userDoc.ref, {
              sessions: updatedSessions
            });
          }
        });
      } else {
        // User not found in organization
      }
    }
  } catch (error) {
    console.error('Error assigning session to user:', error);
    // Don't throw error - session is already created, this is just assignment
  }
};

// Helper: get all domains for a given user (admin or team member)
export const getUserDomains = async (userEmail: string): Promise<string[]> => {
  try {
    // STEP 1: Check if it's an admin user in main collection
    const adminsRef = collection(db, USERS_COLLECTION);
    const adminQuery = query(adminsRef, where('email', '==', userEmail));
    const adminSnapshot = await getDocs(adminQuery);

    if (!adminSnapshot.empty) {
      // Found admin user - read domains from admin document
      const adminDoc = adminSnapshot.docs[0];
      const adminData = adminDoc.data();
      return adminData.domains || [];
    }

    // STEP 2: Check team members across all admins
    const allAdmins = await getDocs(collection(db, USERS_COLLECTION));

    for (const adminDoc of allAdmins.docs) {
      const usersRef = collection(db, USERS_COLLECTION, adminDoc.id, 'users');
      const usersQuery = query(usersRef, where('email', '==', userEmail));
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        // Found team member - read domains from team member document
        const userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        return userData.domains || [];
      }
    }

    // User not found or has no domains
    return [];
  } catch (error) {
    console.error('Error getting user domains:', error);
    throw error;
  }
};

// Add new domain to user's domains array
export const addDomainToUser = async (
  userEmail: string,
  newDomain: string
): Promise<string[]> => {
  try {
    // STEP 1: Check if it's an admin user in main collection
    const adminsRef = collection(db, USERS_COLLECTION);
    const adminQuery = query(adminsRef, where('email', '==', userEmail));
    const adminSnapshot = await getDocs(adminQuery);

    if (!adminSnapshot.empty) {
      // Found admin user - update domains in admin document
      for (const adminDoc of adminSnapshot.docs) {
        const adminData = adminDoc.data();
        const currentDomains = adminData.domains || [];

        let updatedDomains: string[];
        if (!currentDomains.includes(newDomain)) {
          updatedDomains = [...currentDomains, newDomain];
          await updateDoc(doc(db, USERS_COLLECTION, adminDoc.id), {
            domains: updatedDomains
          });
        } else {
          updatedDomains = currentDomains;
        }

        return updatedDomains;
      }
    }

    // STEP 2: Check team members across all admins
    const allAdmins = await getDocs(collection(db, USERS_COLLECTION));

    for (const adminDoc of allAdmins.docs) {
      const usersRef = collection(db, USERS_COLLECTION, adminDoc.id, 'users');
      const usersQuery = query(usersRef, where('email', '==', userEmail));
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        // Found team member - update domains in team member document
        for (const userDoc of usersSnapshot.docs) {
          const userData = userDoc.data();
          const currentDomains = userData.domains || [];

          let updatedDomains: string[];
          if (!currentDomains.includes(newDomain)) {
            updatedDomains = [...currentDomains, newDomain];
            await updateDoc(userDoc.ref, {
              domains: updatedDomains
            });
          } else {
            updatedDomains = currentDomains;
          }

          return updatedDomains;
        }
      }
    }

    // User not found anywhere
    throw new Error(`User ${userEmail} not found in any organization`);
  } catch (error) {
    console.error('Error adding domain to user:', error);
    throw error;
  }
};

export const getSessions = async (): Promise<Session[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, SESSIONS_COLLECTION));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function'
          ? data.createdAt.toDate()
          : new Date(),
        endedAt: data.endedAt && typeof data.endedAt.toDate === 'function'
          ? data.endedAt.toDate()
          : undefined
      } as Session;
    });
  } catch (error) {
    console.error('Error getting sessions:', error);
    throw new Error('Failed to get sessions');
  }
};

/**
 * Get sessions with role-based filtering
 * - Regular users: Only see sessions they created
 * - Admins: See all sessions from their organization
 */
export const getSessionsForUser = async (
  userEmail: string,
  adminId: string,
  isAdmin: boolean
): Promise<Session[]> => {
  try {
    let q;

    if (isAdmin) {
      // Admin users see all sessions from their organization
      q = query(
        collection(db, SESSIONS_COLLECTION),
        where('adminId', '==', adminId)
      );
    } else {
      // Regular users only see sessions they created
      q = query(
        collection(db, SESSIONS_COLLECTION),
        where('createdBy', '==', userEmail),
        where('adminId', '==', adminId)
      );
    }

    const querySnapshot = await getDocs(q);
    const sessions = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function'
          ? data.createdAt.toDate()
          : new Date(),
        endedAt: data.endedAt && typeof data.endedAt.toDate === 'function'
          ? data.endedAt.toDate()
          : undefined
      } as Session;
    });

    // Sort by creation date, newest first
    return sessions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    console.error('Error getting sessions for user:', error);
    throw new Error('Failed to get sessions');
  }
};

export const getSession = async (sessionId: string): Promise<Session | null> => {
  try {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function'
          ? data.createdAt.toDate()
          : new Date(),
        endedAt: data.endedAt && typeof data.endedAt.toDate === 'function'
          ? data.endedAt.toDate()
          : undefined
      } as Session;
    }

    return null;
  } catch (error) {
    console.error('Error getting session:', error);
    throw new Error('Failed to get session data');
  }
};

// User Journeys
export const createUserJourney = async (
  sessionId: string,
  journeyName: string,
  createdBy: string
): Promise<string> => {
  try {
    const journeyData = {
      sessionId,
      journeyName,
      createdBy,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, SESSIONS_COLLECTION, sessionId, 'userJourneys'), journeyData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating user journey:', error);
    throw new Error('Failed to create user journey');
  }
};

export const getUserJourneys = async (sessionId: string): Promise<UserJourney[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, SESSIONS_COLLECTION, sessionId, 'userJourneys'));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function'
          ? data.createdAt.toDate()
          : new Date(),
        endedAt: data.endedAt && typeof data.endedAt.toDate === 'function'
          ? data.endedAt.toDate()
          : undefined
      } as UserJourney;
    });
  } catch (error) {
    console.error('Error getting user journeys:', error);
    throw new Error('Failed to get user journeys');
  }
};

// Images
export const uploadImage = async (
  sessionId: string,
  journeyId: string,
  file: File | null,
  imageUrl: string | null,
  url: string = '',
  note?: string
): Promise<string> => {
  try {
    // Create image document with just the image URL
    const imageData = {
      sessionId,
      journeyId,
      imageUrl: imageUrl || '',
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(
      collection(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images'),
      imageData
    );

    return docRef.id;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image');
  }
};

export const processImage = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  imageUrl: string,
  url: string = '',
  note?: string
): Promise<any> => {
  try {
    // Validate required parameters
    if (!sessionId || !journeyId || !imageId) {
      throw new Error('Missing required parameters: session_id, journey_id, or image_doc_id');
    }

    // Validate image URL
    if (!imageUrl) {
      throw new Error('Invalid image URL');
    }

    // Create FormData object
    const formData = new FormData();

    // Handle different image sources
    if (imageUrl.startsWith('data:image')) {
      // If it's a base64 image, convert to blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch base64 image');
      }
      const blob = await response.blob();
      formData.append('image', blob, 'screencap.jpg');
    } else if (imageUrl.startsWith('blob:')) {
      // If it's a blob URL, fetch and convert to blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob image');
      }
      const blob = await response.blob();
      formData.append('image', blob, 'screencap.jpg');
    } else {
      // If it's a URL, send it as image_url
      formData.append('image_url', imageUrl);
    }

    // Add other form data with validation
    formData.append('url', url || 'https://example.com'); // Provide a default URL if empty
    formData.append('note', note || '');
    formData.append('session_id', sessionId);
    formData.append('journey_id', journeyId);
    formData.append('image_doc_id', imageId);

    // Log the form data for debugging
    console.log('Sending form data:', {
      session_id: sessionId,
      journey_id: journeyId,
      image_doc_id: imageId,
      url: url || 'https://example.com',
      note: note || '',
      has_image: imageUrl.startsWith('data:image') || imageUrl.startsWith('blob:'),
      image_url: !imageUrl.startsWith('data:image') && !imageUrl.startsWith('blob:') ? imageUrl : undefined
    });

    // Call the API endpoint with FormData
    const response = await fetch(`${API_BASE_URL}/upload_and_process_for_aiagents`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to process image: ${response.status}`);
    }

    const result = await response.json();

    // Update the image document with just the image URL
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    await updateDoc(imageRef, {
      imageUrl: result.data?.public_url || ''
    });

    return result;
  } catch (error) {
    console.error('Error processing image:', error);
    throw error;
  }
};

export const getImages = async (
  sessionId: string,
  journeyId: string
): Promise<ImageData[]> => {
  try {
    const querySnapshot = await getDocs(
      collection(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images')
    );

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function'
          ? data.createdAt.toDate()
          : new Date()
      } as ImageData;
    });
  } catch (error) {
    console.error('Error getting images:', error);
    throw new Error('Failed to get images');
  }
};

export const getImage = async (
  sessionId: string,
  journeyId: string,
  imageId: string
): Promise<ImageData | null> => {
  try {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function'
          ? data.createdAt.toDate()
          : new Date()
      } as ImageData;
    }

    return null;
  } catch (error) {
    console.error('Error getting image:', error);
    throw new Error('Failed to get image data');
  }
};

// Process image with quick analysis (no laws) - Step 1 only for UI/UX insights
export const processImageQuickAnalysis = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  imageUrl: string,
  url: string = '',
  note?: string
): Promise<any> => {
  try {
    // Validate required parameters
    if (!sessionId || !journeyId || !imageId) {
      throw new Error('Missing required parameters: session_id, journey_id, or image_doc_id');
    }

    // Validate image URL
    if (!imageUrl) {
      throw new Error('Invalid image URL');
    }

    // Create FormData object
    const formData = new FormData();

    // Handle different image sources
    if (imageUrl.startsWith('data:image')) {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch base64 image');
      }
      const blob = await response.blob();
      formData.append('image', blob, 'screencap.jpg');
    } else if (imageUrl.startsWith('blob:')) {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob image');
      }
      const blob = await response.blob();
      formData.append('image', blob, 'screencap.jpg');
    } else {
      formData.append('image_url', imageUrl);
    }

    formData.append('url', url || 'https://example.com');
    formData.append('note', note || '');
    formData.append('session_id', sessionId);
    formData.append('journey_id', journeyId);
    formData.append('image_doc_id', imageId);

    // Call the step1 endpoint for quick analysis
    const response = await fetch(`${API_BASE_URL}/upload_and_process_step1`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to process image: ${response.status}`);
    }

    const result = await response.json();

    // Extract the dark_pattern_analysis from the response
    let analysisText = '';
    if (result.response && result.response.dark_pattern_analysis) {
      analysisText = result.response.dark_pattern_analysis;
    } else if (result.dark_pattern_analysis) {
      analysisText = result.dark_pattern_analysis;
    } else if (result.data && result.data.dark_pattern_analysis) {
      analysisText = result.data.dark_pattern_analysis;
    } else if (result.data && result.data.response && result.data.response.dark_pattern_analysis) {
      analysisText = result.data.response.dark_pattern_analysis;
    }

    // Format the response as a simple array without laws - just text
    const formattedResponse = [{
      text: analysisText || 'No analysis available',
      kind: '', // No kind for simple display
      type: 'outcome',
      laws: [] // No laws for quick analysis
    }];

    // Get the public URL from result
    let publicUrl = imageUrl;
    if (result.data && result.data.public_url) {
      publicUrl = result.data.public_url;
    } else if (result.public_url) {
      publicUrl = result.public_url;
    }

    // Update the image document
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    await updateDoc(imageRef, {
      imageUrl: publicUrl,
      response: formattedResponse,
      status: 'completed'
    });

    // Auto-create compliance task if issues are found (quick analysis)
    if (hasComplianceIssues(formattedResponse)) {
      // Get session data to find adminId and createdBy
      const sessionDoc = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId));
      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        const adminId = sessionData.adminId;
        const createdBy = sessionData.createdBy;

        if (adminId && createdBy) {
          try {
            await createComplianceTask(
              sessionId,
              journeyId,
              imageId,
              adminId,
              createdBy,
              formattedResponse,
              publicUrl // Include image URL in task
            );
          } catch (taskError) {
            console.error('Error creating compliance task:', taskError);
          }
        }
      }
    }

    return result;
  } catch (error) {
    console.error('Error in quick analysis:', error);
    throw error;
  }
};

// Process image with step 1 only (for conversation mode)
export const processImageStep1 = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  imageUrl: string,
  url: string = '',
  note?: string
): Promise<any> => {
  try {
    // Validate required parameters
    if (!sessionId || !journeyId || !imageId) {
      throw new Error('Missing required parameters: session_id, journey_id, or image_doc_id');
    }

    // Validate image URL
    if (!imageUrl) {
      throw new Error('Invalid image URL');
    }

    // Create FormData object
    const formData = new FormData();

    // Handle different image sources
    if (imageUrl.startsWith('data:image')) {
      // If it's a base64 image, convert to blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch base64 image');
      }
      const blob = await response.blob();
      formData.append('image', blob, 'screencap.jpg');
    } else if (imageUrl.startsWith('blob:')) {
      // If it's a blob URL, fetch and convert to blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob image');
      }
      const blob = await response.blob();
      formData.append('image', blob, 'screencap.jpg');
    } else {
      // If it's a URL, send it as image_url
      formData.append('image_url', imageUrl);
    }

    // Add other form data with validation
    formData.append('url', url || 'https://example.com'); // Provide a default URL if empty
    formData.append('note', note || '');
    formData.append('session_id', sessionId);
    formData.append('journey_id', journeyId);
    formData.append('image_doc_id', imageId);

    // Log the form data for debugging
    console.log('Sending form data to step1 endpoint:', {
      session_id: sessionId,
      journey_id: journeyId,
      image_doc_id: imageId,
      url: url || 'https://example.com',
      note: note || '',
      has_image: imageUrl.startsWith('data:image') || imageUrl.startsWith('blob:'),
      image_url: !imageUrl.startsWith('data:image') && !imageUrl.startsWith('blob:') ? imageUrl : undefined
    });

    // Call the API endpoint with FormData
    const response = await fetch(`${API_BASE_URL}/upload_and_process_step1`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to process image step 1: ${response.status}`);
    }

    const result = await response.json();

    // Extract context from step1_metadata without saving the entire object again
    let extractedContext = '';
    if (result.data?.step1_metadata) {
      const metadata = result.data.step1_metadata;
      if (metadata.context) {
        extractedContext = metadata.context;
      } else if (metadata.Scanner_with_context) {
        extractedContext = metadata.Scanner_with_context;
      } else {
        // Combine relevant fields as fallback
        const textFields = [
          'scanner', 'call_to_action', 'nature_of_design', 'information_organization',
          'typography_and_colour_use', 'footnote_or_information', 'pageName'
        ];

        extractedContext = textFields
          .filter(field => metadata[field])
          .map(field => metadata[field])
          .join(' ');
      }
    }

    // Update the image document with just the extracted context and status
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    await updateDoc(imageRef, {
      imageUrl: result.data?.public_url || '',
      context: extractedContext, // Use the context field directly instead of initialContext
      status: 'step1_completed'
    });

    return result;
  } catch (error) {
    console.error('Error processing image step 1:', error);
    throw error;
  }
};

// Get context-based questions
export const getContextBasedQuestions = async (context: string): Promise<string[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_context_based_questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ context })
    });

    if (!response.ok) {
      throw new Error(`Failed to get context-based questions: ${response.status}`);
    }

    const result = await response.json();
    return result.data?.questions || [];
  } catch (error) {
    console.error('Error getting context-based questions:', error);
    throw error;
  }
};

// Save conversation data to Firebase
export const saveConversation = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  conversation: Conversation
): Promise<void> => {
  try {
    // Format the conversation into a structured context paragraph for analysis
    const formattedQA = formatConversationToContext(conversation);

    // Replace the existing context with the Q&A and save the conversation object
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    await updateDoc(imageRef, {
      conversation, // Save full conversation object
      context: formattedQA // Replace context with Q&A for final analysis
    });
  } catch (error) {
    console.error('Error saving conversation:', error);
    throw error;
  }
};

// Helper function to format conversation into a structured context paragraph
const formatConversationToContext = (conversation: Conversation): string => {
  if (!conversation.questions || !conversation.answers) {
    return '';
  }

  // Combine each question with its corresponding answer
  const combinedQA = conversation.questions.map(question => {
    const answer = conversation.answers[question] || 'No answer provided';
    return `Q: ${question}\nA: ${answer}`;
  }).join('\n\n');

  return combinedQA;
}

/**
 * Check if compliance issues (dark patterns) are found in the analysis response
 */
const hasComplianceIssues = (responseData: any): boolean => {
  if (!responseData) return false;

  // Handle array format (from quick analysis)
  if (Array.isArray(responseData)) {
    for (const item of responseData) {
      if (checkItemForIssues(item)) {
        return true;
      }
    }
    return false;
  }

  // Handle object format (from complete analysis)
  for (const key in responseData) {
    const items = responseData[key];
    if (Array.isArray(items)) {
      for (const item of items) {
        if (checkItemForIssues(item)) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Helper function to check individual item for compliance issues
 * Simple logic: Assume everything is an issue UNLESS it explicitly says "no dark pattern"
 */
const checkItemForIssues = (item: any): boolean => {
  // Always create tasks if there are laws (highest priority)
  if (item.laws && Array.isArray(item.laws) && item.laws.length > 0) {
    return true;
  }

  // Always create tasks if there are dark pattern indicators
  if (item.dark_pattern || item.kind || item.type === 'violation') {
    return true;
  }

  // Check text content
  if (item.text && typeof item.text === 'string') {
    const text = item.text.toLowerCase().trim();

    // ONLY check for explicit negative patterns (no issues found)
    const negativePatterns = [
      'no dark pattern',
      'no dark patterns',
      'no compliance issue',
      'no compliance issues',
      'no violation',
      'no violations',
      'no deceptive',
      'no misleading',
      'no issues',
      'no problems',
      'no concerns',
      'clean design',
      'compliant',
      'no analysis available',
      'analysis not available',
      'no potential issues',
      'appears to be compliant',
      'looks clean',
      'no obvious problems'
    ];

    // If it explicitly says "no issues", don't create task
    for (const negativePattern of negativePatterns) {
      if (text.includes(negativePattern)) {
        return false;
      }
    }

    // For EVERYTHING ELSE with text content - assume it's a compliance issue
    return true;
  }

  // If no text content, don't create task
  return false;
};

/**
 * Extract compliance issues description from response data
 * Only includes the compliance issue text, not law details
 */
const extractComplianceDescription = (responseData: any): string => {
  let description = 'Compliance issues found:\n\n';

  // Helper function to extract issues from an item (text only, no law details)
  const extractFromItem = (item: any) => {
    // Use the compliance issue text only, regardless of whether laws exist
    if (item.laws && Array.isArray(item.laws) && item.laws.length > 0) {
      // Only add the compliance issue text, not law details
      description += `• ${item.text || 'Issue detected'}\n`;
    } else if (item.dark_pattern || item.kind || item.type === 'violation') {
      description += `• ${item.text || 'Dark pattern detected'}\n`;
    } else if (checkItemForIssues(item)) {
      description += `• ${item.text || 'Compliance issue detected'}\n`;
    }
  };

  // Handle array format (from quick analysis)
  if (Array.isArray(responseData)) {
    responseData.forEach(extractFromItem);
  } else {
    // Handle object format (from complete analysis)
    for (const key in responseData) {
      const items = responseData[key];
      if (Array.isArray(items)) {
        items.forEach(extractFromItem);
      }
    }
  }

  return description;
};

/**
 * Get or create "Dark Patterns" board for the organization
 */
const getOrCreateIssuesBoard = async (adminId: string, createdBy: string): Promise<string> => {
  try {
    // Get existing boards
    const boards = await getBoards(adminId);

    // Look for existing "Dark Patterns" board
    const existingBoard = boards.find(board =>
      board.title.toLowerCase().includes('dark pattern') ||
      board.title.toLowerCase().includes('compliance')
    );

    if (existingBoard) {
      return existingBoard.id;
    }

    // Create new "Dark Patterns" board
    const newBoard = await createBoard(
      adminId,
      'Dark Patterns',
      'Automatically created board for tracking compliance issues and dark patterns found in analysis',
      createdBy,
      '⚠️'
    );

    return newBoard.id;
  } catch (error) {
    console.error('Error getting or creating issues board:', error);
    throw error;
  }
};

/**
 * Automatically create task when compliance issues are found
 */
const createComplianceTask = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  adminId: string,
  createdBy: string,
  responseData: any,
  imageUrl?: string
): Promise<void> => {
  try {
    // Get session and journey details for task name
    const sessionDoc = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId));
    const journeyDoc = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId));

    if (!sessionDoc.exists() || !journeyDoc.exists()) {
      console.error('Session or journey document not found for task creation');
      return;
    }

    const sessionData = sessionDoc.data();
    const journeyData = journeyDoc.data();

    // Create task name: sessionName_journeyName_imageId
    const taskName = `${sessionData.sessionName}_${journeyData.journeyName}_${imageId}`;

    // Get compliance description
    const description = extractComplianceDescription(responseData);

    // Get or create board
    const boardId = await getOrCreateIssuesBoard(adminId, createdBy);

    // Calculate due date (next day)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Create task data with image support - Always include imageUrl field
    const taskData: any = {
      description,
      dueDate: tomorrow,
      tags: [],
      // Always include imageUrl, even if empty string, to ensure it's in the task
      imageUrl: imageUrl || ''
    };

    // Filter out any undefined values
    const cleanTaskData = Object.fromEntries(
      Object.entries(taskData).filter(([_, value]) => value !== undefined)
    );

    await createTask(
      adminId,
      boardId,
      taskName,
      createdBy,
      'todo',
      cleanTaskData
    );
  } catch (error) {
    console.error('Error creating compliance task:', error);
    // Don't throw - task creation failure shouldn't break image processing
  }
};

// Complete image processing after conversation
export const completeImageProcessing = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  imageUrl: string,
  url: string = '',
  note?: string
): Promise<any> => {
  try {
    // Get the latest image data to ensure we have the updated context
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    const imageDoc = await getDoc(imageRef);

    if (!imageDoc.exists()) {
      throw new Error('Image document not found');
    }

    const imageData = imageDoc.data();
    const context = imageData.context || '';

    // Using context for final processing

    // Call the API endpoint with JSON data
    const response = await fetch(`${API_BASE_URL}/get_image_metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_url: imageUrl,
        url: url || 'https://example.com',
        note: note || '',
        session_id: sessionId,
        journey_id: journeyId,
        image_doc_id: imageId,
        context: context
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to process image: ${response.status}`);
    }

    const result = await response.json();

    // First, try to find the response data in the result
    let responseData = null;

    if (result.data?.processed_metadata?.response) {
      // Standard location
      responseData = result.data.processed_metadata.response;
    } else if (result.data?.response) {
      // Alternative location
      responseData = result.data.response;
    } else if (result.processed_metadata?.response) {
      // Another possible location
      responseData = result.processed_metadata.response;
    }

    if (!responseData) {
      // API returned no response data, using empty response
      responseData = {}; // Empty response object
    }

    // Update the image document with the final response
    await updateDoc(imageRef, {
      imageUrl: result.data?.public_url || imageUrl,
      response: responseData,
      status: 'completed'
    });

    // Updated image document with response data

    // Auto-create compliance task if issues are found
    if (hasComplianceIssues(responseData)) {
      // Get session data to find adminId and createdBy
      const sessionDoc = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId));
      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        const adminId = sessionData.adminId;
        const createdBy = sessionData.createdBy;

        if (adminId && createdBy) {
          try {
            await createComplianceTask(
              sessionId,
              journeyId,
              imageId,
              adminId,
              createdBy,
              responseData,
              result.data?.public_url || imageUrl // Include image URL in task
            );
          } catch (taskError) {
            console.error('Error creating compliance task:', taskError);
          }
        }
      }
    }

    return result;
  } catch (error) {
    console.error('Error completing image processing:', error);
    throw error;
  }
};

// Chat with Image - Add a new endpoint for chatting with an image
export const chatWithImage = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  question: string
): Promise<any> => {
  try {
    // Get the image data first
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    const imageDoc = await getDoc(imageRef);

    if (!imageDoc.exists()) {
      throw new Error('Image document not found');
    }

    const imageData = imageDoc.data();
    const imageUrl = imageData.imageUrl || '';

    // Get existing chat history or initialize a new one
    const chatHistory = imageData.chatHistory || [];

    // Call the API endpoint
    const response = await fetch(`${API_BASE_URL}/chat_with_image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: sessionId,
        journey_id: journeyId,
        image_id: imageId,
        image_url: imageUrl,
        question: question
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to chat with image: ${response.status}`);
    }

    const result = await response.json();

    // Extract the response text
    let responseText = '';
    if (result.data && result.data.response) {
      responseText = result.data.response;
    } else if (result.response) {
      responseText = result.response;
    } else if (result.answer) {
      responseText = result.answer;
    } else {
      responseText = 'No response received';
    }

    // Add the QA pair to chat history
    const newEntry = {
      question,
      answer: responseText,
      timestamp: new Date().toISOString()
    };

    // Update the image document with the updated chat history
    await updateDoc(imageRef, {
      chatHistory: [...chatHistory, newEntry]
    });

    // Return the updated result with the properly formatted response
    return {
      ...result,
      formattedResponse: responseText
    };
  } catch (error) {
    console.error('Error chatting with image:', error);
    throw error;
  }
};

// Regenerate response with feedback
export const regenerateWithFeedback = async (
  sessionId: string,
  journeyId: string,
  imageId: string,
  feedback: string
): Promise<any> => {
  try {
    // Get the image data first
    const imageRef = doc(db, SESSIONS_COLLECTION, sessionId, 'userJourneys', journeyId, 'images', imageId);
    const imageDoc = await getDoc(imageRef);

    if (!imageDoc.exists()) {
      throw new Error('Image document not found');
    }

    const imageData = imageDoc.data();
    const imageUrl = imageData.imageUrl || '';
    const outcome = imageData.response || {};

    // Call the API endpoint
    const response = await fetch(`${API_BASE_URL}/regerate_based_on_feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        feedback: feedback,
        outcome: outcome,
        imageUrl: imageUrl,
        imageId: imageId,
        session_id: sessionId,
        journey_id: journeyId
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to regenerate response: ${response.status}`);
    }

    const result = await response.json();
    // Regenerate API response received

    // Extract the formatted response which is the final data we want to save
    let responseData = null;

    // Check for response in different locations based on API response format
    if (result.response) {
      // Directly use the formatted response if available
      responseData = result.response;
    } else if (result.revised_outcome) {
      // Or use the revised outcome if that's what's returned
      responseData = result.revised_outcome;
    } else if (result.data && result.data.response) {
      // For nested data structure
      responseData = result.data.response;
    }

    if (!responseData) {
      console.error('No response data in API result', result);
      throw new Error('API returned no valid response data');
    }

    // Save the feedback and updated response in the document
    await updateDoc(imageRef, {
      response: responseData,
      feedback: feedback, // Store the feedback that was provided
      lastRefinedAt: serverTimestamp() // Track when the refinement was done
    });

    // Updated image document with refined response data

    // Return the result with the formatted response for immediate display
    return {
      ...result,
      formattedResponse: responseData
    };
  } catch (error) {
    console.error('Error regenerating response:', error);
    throw error;
  }
};